import { useEffect, useState } from "react";
import {
  Center,
  Group,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import ReactECharts from "echarts-for-react";
import { api } from "../lib/api";

interface LatestPrice {
  retailerId: string;
  retailerName: string;
  effectivePrice: string;
  observedAt: string;
}

interface Product {
  productId: string;
  productName: string;
  brandName: string;
  latestPrices: LatestPrice[];
}

interface PriceGap {
  productId: string;
  retailerId: string;
  effectivePrice: string;
  gapAmount: string;
  gapPct: string;
}

interface HistoricalPoint {
  timestamp: string;
  avgPrice: string;
}

interface HistoricalSeries {
  productId: string;
  productName: string;
  dataPoints: HistoricalPoint[];
}

interface ComparisonResponse {
  products: Product[];
  priceGaps: PriceGap[];
  historicalSeries: HistoricalSeries[];
}

const PRODUCT_COLORS = [
  "#228be6", "#fa5252", "#40c057", "#fab005", "#7950f2",
];

const formatCurrency = (value: string): string => {
  const parsed = parseFloat(value);
  if (!Number.isFinite(parsed)) return "-";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(parsed);
};

export function ComparisonPage() {
  const [data, setData] = useState<ComparisonResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const endDate = new Date().toISOString().slice(0, 10);
    const startDate = new Date(Date.now() - 30 * 86_400_000)
      .toISOString()
      .slice(0, 10);

    api
      .get("/grid", { params: { page: 1, page_size: 4 }, signal })
      .then(({ data: gridData }) => {
        const productIds = gridData.rows
          ?.slice(0, 4)
          .map((r: { productId: string }) => r.productId);
        if (!productIds?.length) {
          if (!signal.aborted) setLoading(false);
          return;
        }
        return api.get<ComparisonResponse>("/comparison", {
          params: {
            product_id: productIds,
            start_date: startDate,
            end_date: endDate,
          },
          signal,
        });
      })
      .then((resp) => {
        if (resp && !signal.aborted) setData(resp.data);
      })
      .catch((err) => {
        if (signal.aborted) return;
        notifications.show({
          title: "Failed to load comparison",
          message: err instanceof Error ? err.message : "Network error",
          color: "red",
        });
      })
      .finally(() => {
        if (!signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, []);

  if (loading) {
    return (
      <Stack gap="md">
        <Title order={2}>Competitive Comparison</Title>
        <Skeleton height={300} radius="sm" />
        <Skeleton height={300} radius="sm" />
      </Stack>
    );
  }

  if (!data || data.products.length === 0) {
    return (
      <Stack gap="md">
        <Title order={2}>Competitive Comparison</Title>
        <Center py={60}>
          <Text c="dimmed">
            No products available for comparison. Add products from the Grid
            page.
          </Text>
        </Center>
      </Stack>
    );
  }

  // Get unique retailers across all products
  const retailers = new Map<string, { id: string; name: string }>();
  for (const product of data.products) {
    for (const lp of product.latestPrices) {
      retailers.set(lp.retailerId, {
        id: lp.retailerId,
        name: lp.retailerName,
      });
    }
  }
  const retailerList = Array.from(retailers.values());

  // Historical chart
  const chartOption = {
    tooltip: {
      trigger: "axis" as const,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      formatter: (params: any) => {
        const arr = Array.isArray(params) ? params : [params];
        if (!arr.length) return "";
        const date = new Date(arr[0].value[0]).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });
        const lines = arr.map(
          (p: { color: string; seriesName: string; value: [string, number] }) =>
            `<span style="color:${p.color}">\u25CF</span> ${p.seriesName}: ${formatCurrency(String(p.value[1]))}`,
        );
        return `<b>${date}</b><br/>${lines.join("<br/>")}`;
      },
    },
    legend: { bottom: 0, type: "scroll" as const },
    grid: { left: 80, right: 20, top: 20, bottom: 60 },
    xAxis: { type: "time" as const },
    yAxis: {
      type: "value" as const,
      axisLabel: {
        formatter: (val: number) =>
          new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(val),
      },
    },
    series: data.historicalSeries.map((s, i) => ({
      name: s.productName,
      type: "line" as const,
      smooth: true,
      symbol: "circle",
      symbolSize: 4,
      color: PRODUCT_COLORS[i % PRODUCT_COLORS.length],
      data: s.dataPoints.map((dp) => [dp.timestamp, parseFloat(dp.avgPrice)]),
    })),
  };

  return (
    <Stack gap="lg">
      <Title order={2}>Competitive Comparison</Title>

      {/* Latest prices table */}
      <div>
        <Text size="sm" fw={600} mb="xs">
          Current Prices
        </Text>
        <Table.ScrollContainer minWidth={500}>
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Product</Table.Th>
                {retailerList.map((r) => (
                  <Table.Th key={r.id} style={{ textAlign: "center" }}>
                    {r.name}
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.products.map((product) => (
                <Table.Tr key={product.productId}>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {product.productName}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {product.brandName}
                    </Text>
                  </Table.Td>
                  {retailerList.map((retailer) => {
                    const lp = product.latestPrices.find(
                      (p) => p.retailerId === retailer.id,
                    );
                    return (
                      <Table.Td key={retailer.id} style={{ textAlign: "center" }}>
                        {lp ? (
                          <Text size="sm" fw={500}>
                            {formatCurrency(lp.effectivePrice)}
                          </Text>
                        ) : (
                          <Text size="sm" c="dimmed">
                            -
                          </Text>
                        )}
                      </Table.Td>
                    );
                  })}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </div>

      {/* Price gap section */}
      {data.priceGaps.length > 0 && (
        <div>
          <Text size="sm" fw={600} mb="xs">
            Price Gaps
          </Text>
          <Group gap="md">
            {data.priceGaps.slice(0, 8).map((gap, i) => {
              const product = data.products.find(
                (p) => p.productId === gap.productId,
              );
              const pct = parseFloat(gap.gapPct);
              return (
                <Stack key={i} gap={2} align="center">
                  <Text size="xs" c="dimmed" lineClamp={1}>
                    {product?.productName ?? "Unknown"}
                  </Text>
                  <Text
                    size="sm"
                    fw={600}
                    c={pct > 0 ? "red" : pct < 0 ? "green" : "dimmed"}
                  >
                    {pct > 0 ? "+" : ""}
                    {pct.toFixed(1)}%
                  </Text>
                </Stack>
              );
            })}
          </Group>
        </div>
      )}

      {/* Historical chart */}
      {data.historicalSeries.length > 0 && (
        <div>
          <Text size="sm" fw={600} mb="xs">
            30-Day Price Trend
          </Text>
          <ReactECharts option={chartOption} style={{ height: 350 }} />
        </div>
      )}
    </Stack>
  );
}
